import React from "react";
import RegisterPage, { STATUS_BADGES, PRIORITY_BADGES, StatCard } from "../components/RegisterPage";
import { useHSE } from "../lib/store";
import { calculateDaysOverdue } from "../lib/hseCalc";

export default function ActionPlan() {
  const { data, siteNames } = useHSE();
  const rows = data.actions || [];

  const isOverdue = (r) => r.status !== "Closed" && calculateDaysOverdue(r.dueDate, r.status) > 0;

  const critical = rows.filter((r) => r.priority === "Critical").length;
  const overdue = rows.filter(isOverdue).length;
  const open = rows.filter((r) => r.status === "Open").length;
  const inProgress = rows.filter((r) => r.status === "In Progress").length;
  const closed = rows.filter((r) => r.status === "Closed").length;

  const Top = (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <StatCard label="Open" value={open} tone="warning" testid="act-open" />
      <StatCard label="In Progress" value={inProgress} tone="info" testid="act-progress" />
      <StatCard label="Overdue" value={overdue} tone="critical" testid="act-overdue" />
      <StatCard label="Closed" value={closed} tone="safe" testid="act-closed" />
      <StatCard label="Critical Priority" value={critical} tone="critical" testid="act-critical" />
    </div>
  );

  return (
    <RegisterPage
      title="Action Plan"
      subtitle="Corporate corrective & preventive actions — Incident · Audit · Inspection · SOR · NCR · Client · Mgmt Review"
      storeKey="actions"
      idPrefix="ACT"
      topPanels={Top}
      columns={[
        { key: "id", label: "Action ID" },
        { key: "source", label: "Source" },
        { key: "refId", label: "Reference", render: (r) => r.refId ? <span className="font-mono text-[11px] text-[#082B4D]">{r.refId}</span> : <span className="text-slate-300">—</span> },
        { key: "site", label: "Site" },
        { key: "department", label: "Dept / Contractor" },
        { key: "description", label: "Description" },
        { key: "priority", label: "Priority" },
        { key: "owner", label: "Owner" },
        { key: "dueDate", label: "Due" },
        { key: "daysOverdue", label: "Overdue", render: (r) => {
            const d = calculateDaysOverdue(r.dueDate, r.status);
            return d > 0 && r.status !== "Closed"
              ? <span className="text-rose-600 font-semibold tabular">{d}d</span>
              : <span className="text-slate-400">—</span>;
          }
        },
        { key: "status", label: "Status", render: (r) => {
            const overdueNow = isOverdue(r);
            const val = overdueNow ? "Overdue" : r.status;
            const m = STATUS_BADGES[val] || { bg: "#F1F5F9", color: "#455560" };
            return <span className="cmd-pill" style={{ background: m.bg, color: m.color }}>{val}</span>;
          }
        },
        { key: "verification", label: "Verified", render: (r) => r.verification ? <span className="text-[11px] text-[#18B84F]">✓ {r.verification}</span> : <span className="text-slate-300 text-[11px]">Pending</span> },
        { key: "approval", label: "Approved", render: (r) => r.approval ? <span className="text-[11px] text-[#18B84F]">✓ {r.approval}</span> : <span className="text-slate-300 text-[11px]">Pending</span> },
        { key: "completionDate", label: "Completed" },
      ]}
      badgeMap={{ priority: PRIORITY_BADGES }}
      formFields={[
        { key: "id", label: "Action ID" },
        { key: "source", label: "Source", type: "select", options: ["Incident", "Audit", "Inspection", "SOR", "NCR", "Client Comment", "Management Review", "Observation"] },
        { key: "refId", label: "Reference ID" },
        { key: "site", label: "Site", type: "select", options: siteNames },
        { key: "department", label: "Department / Contractor" },
        { key: "description", label: "Action Description", type: "textarea", full: true },
        { key: "rootCause", label: "Root Cause (if applicable)", type: "textarea", full: true },
        { key: "correctiveAction", label: "Corrective Action", type: "textarea", full: true },
        { key: "preventiveAction", label: "Preventive Action", type: "textarea", full: true },
        { key: "priority", label: "Priority", type: "select", options: ["Low", "Medium", "High", "Critical"] },
        { key: "owner", label: "Responsible Person" },
        { key: "dueDate", label: "Target Date", type: "date" },
        { key: "completionDate", label: "Completion Date", type: "date" },
        { key: "verification", label: "Verified By (HSE)" },
        { key: "approval", label: "Approved By (Manager)" },
        { key: "status", label: "Status", type: "select", options: ["Open", "In Progress", "Closed"] },
      ]}
    />
  );
}
